import { atom } from 'recoil'
import type {
  AiHeadlineArticleStructureConnection,
  ChataiConversationsConnection,
  ChataiLog,
  ChataiLogsConnection,
  ChataiMessage,
  ChataiUsage,
} from '~/graphql'
import type { AiHeadlineListItem } from '~/hooks/useAiHeadlineList'

export type Message = Pick<ChataiMessage, 'role' | 'content'> &
  Partial<Pick<ChataiMessage, 'id' | 'parent' | 'children'>>

/**
 * Chat AI 会話Id
 */
export const chataiConversationIdState = atom<string | null>({
  key: 'ChataiConversationId',
  default: null,
})

/**
 * Chat AI 会話 Fetching
 */
export const chataiConversationIsFetchingState = atom<boolean>({
  key: 'ChataiConversationIsFetching',
  default: false,
})

/**
 * Chat AI 会話メッセージ
 */
export const chataiConversationMessagesState = atom<Message[]>({
  key: 'ChataiConversationMessages',
  default: [],
})

/**
 * Chat AI 会話作成 ローディング
 */
export const chataiCreateConversationIsLoadingState = atom<boolean>({
  key: 'ChataiCreateConversationIsLoading',
  default: false,
})

/**
 * Chat AI 会話作成 ストリームメッセージ
 */
export const chataiCreateConversationStreamMessageState = atom<string>({
  key: 'ChataiCreateConversationStreamMessage',
  default: '',
})

/**
 * Chat AI 会話リスト ページリスト
 */
export const chataiConversationsPagesState = atom<ChataiConversationsConnection[]>({
  key: 'ChataiConversationsPages',
  default: [],
})

/**
 * Chat AI 会話リスト ローディング
 */
export const chataiConversationsIsLoadingState = atom<boolean>({
  key: 'ChataiConversationsIsLoading',
  default: false,
})

/**
 * Chat AI 会話リスト ページ
 */
export const chataiConversationsPageState = atom<number>({
  key: 'ChataiConversationsPage',
  default: 1,
})

/**
 * Chat AI 会話リスト ページサイズ
 */
export const chataiConversationsPageSizeState = atom<number>({
  key: 'ChataiConversationsPageSize',
  // default: 28,
  default: 5,
})

/**
 * Chat AI 会話リスト 総数
 */
export const chataiConversationsTotalState = atom<number>({
  key: 'ChataiConversationsTotal',
  default: 0,
})

/**
 * Chat AI 入力エリアの高さ
 */
export const chataiTextareaHeightState = atom<number>({
  key: 'ChataiTextareaHeight',
  default: 135,
})

/**
 * Chat AI Drawer Open
 */
export const chataiDrawerOpenState = atom<boolean>({
  key: 'ChataiDrawerOpen',
  default: true,
})

/**
 * Chat AI タイトル生成 ローディング
 */
export const chataiGenerateTitleLoadingsState = atom({
  key: 'ChataiGenerateTitleLoadings',
  default: new Map<string, boolean>(),
})

/**
 * Chat AI メッセージの最下部にスクロール維持するかどうか
 */
export const chataiStreamKeepBottomState = atom({
  key: 'ChataiStreamKeepBottom',
  default: false,
})

/**
 * Chat AI 利用状況
 */
export const chataiUsagesState = atom<ChataiUsage[]>({
  key: 'ChataiUsages',
  default: [],
})

/**
 * Chat AI 利用状況 ログ
 */
export const chataiUsageLogsState = atom<ChataiLog[]>({
  key: 'ChataiUsageLogs',
  default: [],
})

type ExchnageRate = { rate: number; updatedAt: number }

/**
 * Chat AI 為替情報
 */
export const chataiExchangeRateState = atom<ExchnageRate | null>({
  key: 'ChataiExchangeRate',
  default: null,
})

/**
 * Chat AI ログ ページリスト
 */
export const chataiLogsPagesState = atom<ChataiLogsConnection[]>({
  key: 'ChataiLogsPages',
  default: [],
})

/**
 * Chat AI ログ ローディング
 */
export const chataiLogsIsLoadingState = atom<boolean>({
  key: 'ChataiLogsIsLoading',
  default: false,
})

/**
 * Chat AI ログ ページ
 */
export const chataiLogsPageState = atom<number>({
  key: 'ChataiLogsPage',
  default: 1,
})

/**
 * Chat AI ログ ページサイズ
 */
export const chataiLogsPageSizeState = atom<number>({
  key: 'ChataiLogsPageSize',
  // default: 28,
  default: 5,
})

/**
 * Chat AI ログ 総数
 */
export const chataiLogsTotalState = atom<number>({
  key: 'ChataiLogsTotal',
  default: 0,
})

/**
 * Chat AI Log Drawer Open
 */
export const chataiLogDrawerOpenState = atom<boolean>({
  key: 'ChataiLogDrawerOpen',
  default: false,
})

export const aiHeadlineArticleStructure = atom<AiHeadlineArticleStructureConnection | undefined>({
  key: 'aiHeadlineArticleStructure',
  default: undefined,
})

export const aiHeadlineIsFethingArticleStructure = atom<boolean>({
  key: 'aiHeadlineIsFethingArticleStructure',
  default: false,
})

export const aiHeadlineIsDraggingState = atom<boolean>({
  key: 'aiHeadlineIsDragging',
  default: false,
})

/**
 * AI 記事作成 見出し作成 見出しリスト
 */
export const aiHeadlineListState = atom<AiHeadlineListItem[] | null>({
  key: 'AiHeaderList',
  default: null,
})

/**
 * AI 記事作成 見出し作成 APIからのデフォルト見出しリスト
 */
export const aiHeadlineDefaultListState = atom<AiHeadlineListItem[] | null>({
  key: 'AiHeaderDefaultList',
  default: null,
})

/**
 * AI 記事作成 見出し作成 選択中の見出し
 */
export const aiHeadlineSelectedItemState = atom<string | null>({
  key: 'AiHeadlineSelectedItem',
  default: null,
})
